import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { splitHighlight } from './highlight';

export interface SearchRowProps {
  /** Text shown on the row (a recent query, a popular event, or a result). */
  label: string;
  /** When set, every case-insensitive occurrence of it in `label` is bolded. */
  highlight?: string;
  onClick: () => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * One tappable search row: a placeholder avatar, a label (optionally with the
 * matched query bolded), and a trailing chevron. Shared by the recent, popular,
 * related and result lists so every list reads identically (ARC-113).
 */
export function SearchRow({ label, highlight, onClick, className, ...rest }: SearchRowProps) {
  const segments = splitHighlight(label, highlight ?? '');

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={rest['data-testid']}
      className={cn(
        'flex items-center gap-3 w-full h-[56px] rounded-[12px] bg-surface-soft px-2.5 text-left cursor-pointer border-none',
        className,
      )}
    >
      <span className="shrink-0 size-11 rounded-full bg-border" aria-hidden />
      <span className="flex-1 min-w-0 truncate text-[16px] text-ink">
        {segments.map((seg, i) =>
          seg.match ? (
            <strong key={i} className="font-semibold">
              {seg.text}
            </strong>
          ) : (
            <span key={i} className="font-normal">
              {seg.text}
            </span>
          ),
        )}
      </span>
      <ChevronRight className="shrink-0 size-5 text-ink/70" />
    </button>
  );
}
